import { yupResolver } from '@hookform/resolvers/yup';
import { Fragment, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from '@krea/common/design-system/Form';
import { settings } from '@krea/common/settings';
import { Label } from '@krea/common/shared-components/fields/label';
import { Helper } from '@krea/common/shared-components/helper';
import { Text } from '@krea/common/shared-components/text';
import { useGetCachedCurrentLoanApplication } from '@krea/common/store/current-loan-application/hooks';
import { useGetOneFieldRequest } from '@krea/common/store/field-requests/hooks/queries';
import { IGuarantor } from '@krea/common/types/fieldRequests/guarantor';
import {
  IBoardMember,
  LoanApplicationStates,
} from '@krea/common/types/loanApplication';
import { COMPANY_TYPE, COUNTRY_CODE } from '@krea/common/utils';

import FieldRequestPreviewHeader from '../../FieldRequestPreviewHeader';
import { GuarantorPreviewItem } from '../GuarantorPreviewItem';

import { GuarantorsFormItem } from './GuarantorsFormItem';
import { guarantorsFormSchema } from './formHelpers';

export interface IGuarantorsFormProps {
  fieldRequestID: number;
  withInputLabel?: boolean;
  isPreviewMode: boolean;
}

export const GuarantorsForm = ({
  fieldRequestID,
  withInputLabel,
  isPreviewMode,
}: IGuarantorsFormProps) => {
  const { t } = useTranslation();
  const form = useForm({
    resolver: yupResolver(guarantorsFormSchema(t)),
    defaultValues: {
      guarantors: [],
    },
  });

  const { data: { contactPerson, applyingCompany, state } = {} } =
    useGetCachedCurrentLoanApplication();
  const { data: frData, isLoading } = useGetOneFieldRequest(fieldRequestID);

  const displayName = frData?.displayName;

  const boardMembers = applyingCompany?.boardMembers || [];

  const companyType = applyingCompany?.customerCompany?.typeDisplayName;

  const isBidSelected = state === LoanApplicationStates.BID_SELECTED;
  const isSoleTrader = companyType === COMPANY_TYPE.SOLE_TRADER;

  const explanationText = useMemo(() => {
    if (isSoleTrader) {
      return t('iaq.guarantor.explanation.text.soleTrader');
    }

    if (companyType === COMPANY_TYPE.PARTNERSHIP) {
      return t('iaq.guarantor.explanation.text.partnership');
    }

    return t('iaq.guarantor.explanation.text.default');
  }, [companyType, isSoleTrader, t]);

  const guarantors = useMemo(
    () => frData?.submittedData?.guarantors || [],
    [frData?.submittedData?.guarantors],
  );

  const isFinland = settings.countryCode === COUNTRY_CODE.FI;

  // Sort such that unselected board members are at the bottom of the list
  const boardMembersSorted = boardMembers
    .filter((boardMember: IBoardMember) =>
      isFinland
        ? boardMember.ssn.substring(0, 7) !== contactPerson?.ssn.substring(0, 7)
        : boardMember.ssn !== contactPerson?.ssn,
    )
    .sort((a: IBoardMember) =>
      isFinland
        ? guarantors.some(
            (g: IGuarantor) =>
              g.ssn.substring(0, 7).toString() ===
              a.ssn.substring(0, 7).toString(),
          )
          ? -1
          : 1
        : guarantors.some((g: IGuarantor) => g.ssn === a.ssn)
          ? -1
          : 1,
    );

  useEffect(() => {
    if (!isLoading && guarantors) {
      form.reset(
        {
          guarantors,
        },
        {
          keepDirty: false,
          keepDirtyValues: false,
        },
      );
    }
  }, [form, guarantors, isLoading]);

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          className={''}
          label={displayName}
          value={
            guarantors.length > 0 &&
            guarantors.map((guarantor: IGuarantor, index: number) => (
              <Fragment key={index}>
                <GuarantorPreviewItem guarantor={guarantor} />
              </Fragment>
            ))
          }
        />
      ) : (
        <>
          {!isBidSelected || isSoleTrader ? (
            <>
              <Text size="md" className="pb-3">
                <span className="align-middle">{explanationText}</span>
                <Helper
                  className="align-middle ml-1"
                  content={t('iaq.guarantor.explanation.tooltip')}
                />
              </Text>
            </>
          ) : null}
          <form className="input-form tw-mt-4" name="GuarantorsForm">
            <Form {...form}>
              {withInputLabel && (
                <Label className="tw-mb-2">{frData.displayName}</Label>
              )}

              {/* contact person */}
              <GuarantorsFormItem
                control={form.control}
                fieldRequestID={fieldRequestID}
                contactPerson={contactPerson}
                isContactPerson
                isDisabled={isSoleTrader}
                submittedGuarantors={guarantors}
                manuallyAddGuarantor={false}
              />

              {/* board members */}
              {boardMembersSorted.map((boardMember: IBoardMember) => (
                <GuarantorsFormItem
                  key={boardMember.ssn + boardMember.id}
                  guarantor={boardMember}
                  guarantorFromBoardMembers={true}
                  control={form.control}
                  fieldRequestID={fieldRequestID}
                  submittedGuarantors={guarantors}
                  manuallyAddGuarantor={false}
                />
              ))}

              {/* manually added non-board member guarantors */}
              {guarantors &&
                guarantors.length > 0 &&
                guarantors
                  .filter(
                    (guarantor: IGuarantor) =>
                      guarantor.ssn !== contactPerson?.ssn &&
                      !boardMembers.find((boardMember: IBoardMember) =>
                        isFinland
                          ? boardMember.ssn.substring(0, 7) ===
                            guarantor.ssn.substring(0, 7)
                          : boardMember.ssn === guarantor.ssn,
                      ),
                  )
                  .map((guarantor: any) => (
                    <GuarantorsFormItem
                      key={guarantor.ssn + guarantor.id}
                      guarantor={guarantor}
                      guarantorFromBoardMembers={false}
                      control={form.control}
                      fieldRequestID={fieldRequestID}
                      submittedGuarantors={guarantors}
                      manuallyAddGuarantor={false}
                    />
                  ))}

              {/* functionality for manually adding guarantors */}
              {(!boardMembers ||
                (boardMembers && boardMembers.length === 0)) && (
                <GuarantorsFormItem
                  control={form.control}
                  fieldRequestID={fieldRequestID}
                  submittedGuarantors={guarantors}
                  manuallyAddGuarantor={true}
                />
              )}
            </Form>
          </form>
        </>
      )}
    </>
  );
};
